import React, { memo, useState } from 'react'
import { DataTablePageEvent } from 'primereact/datatable'
import { Link } from 'react-router-dom'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'

import Text from '../../../../ui/Text'
import { isPartnerDomain } from '../../../../lib/currentDomain'
import {
    enhancedApi as webApi,
    MeasurementDevicesGetFilteredPagedApiArg,
    PagedResultOfMeasurementDevicesGetModel,
    MeasurementDevicesGetModel,
} from '../../../redux/api/web-api'
import useText from '../../../hooks/useText'
import { Table } from '../../../uikit/Table'
import { dateLocalize } from '../../../../lib/date-localize'
import { getProductName } from '../../../services/getProductName'

type MeasurementDevicesTableFilter = Pick<MeasurementDevicesGetFilteredPagedApiArg, 'page' | 'noOfItems'>

export type MeasurementDevicesTableWidgetProps = {
    data: PagedResultOfMeasurementDevicesGetModel
    isLoading?: boolean
    openAddModal?: () => void
    removeDevice?: (device: MeasurementDevicesGetModel) => void
    filter: MeasurementDevicesTableFilter
    setFilter: (filter: MeasurementDevicesTableFilter) => void
    style?: React.CSSProperties
}

export const MeasurementDevicesTableWidget = memo(function MeasurementDevicesTableWidget({
    data,
    isLoading,
    removeDevice,
    filter,
    setFilter,
}: MeasurementDevicesTableWidgetProps): React.ReactElement {
    const text = useText()

    const prefetchDeviceGraph = webApi.usePrefetch('loadBalancerGroupGetSnapshotByDeviceId')

    const [lazyState, setLazyState] = useState({
        first: (filter?.page ?? 0) * (filter?.noOfItems ?? 15),
        rows: filter?.noOfItems ?? 15,
        page: filter?.page ?? 0,
    })

    const onPage = (event: DataTablePageEvent) => {
        const newPage = event.first / event.rows
        setLazyState({
            first: event.first,
            rows: event.rows,
            page: newPage,
        })
        setFilter({ page: newPage, noOfItems: event.rows })
    }

    return (
        <Table
            value={data?.items || []}
            loading={isLoading}
            lazy
            paginator
            first={lazyState.first}
            rows={lazyState.rows}
            totalRecords={data?.totalCount ?? 0}
            onPage={onPage}
        >
            <Column
                header={text('lblSerialNumberShort')}
                body={(measurementdevice: MeasurementDevicesGetModel) => (
                    <Link
                        to={`/measurementDevice/${measurementdevice.id}`}
                        state={{ fromMeasurementDeviceList: true }}
                        data-testid={`table-tr-serial-${measurementdevice.serialNumber}-btn`}
                        onMouseEnter={() => prefetchDeviceGraph({ measurementDeviceId: measurementdevice.id! })}
                    >
                        {measurementdevice.serialNumber}
                    </Link>
                )}
            />
            <Column
                header={text('lblStatus')}
                body={(measurementdevice: MeasurementDevicesGetModel) => (
                    <>
                        {measurementdevice.status == 'Online' ? (
                            <span className="fa fa-wifi greenFont" title="Online"></span>
                        ) : (
                            <span className="fa fa-wifi redFont" title="Offline"></span>
                        )}
                    </>
                )}
            />
            <Column
                header={text('lblLastSeen')}
                body={(measurementdevice: MeasurementDevicesGetModel) => dateLocalize(measurementdevice?.lastSeen)}
            />
            <Column
                header={text('lblName')}
                body={(measurementdevice: MeasurementDevicesGetModel) =>
                    measurementdevice.name || measurementdevice.serialNumber
                }
            />
            <Column field="description" header={text('lblDescription')} />
            <Column field="companyName" header={text('lblOrganisation')} />
            <Column
                header={text('lblProduct')}
                body={(measurementdevice: MeasurementDevicesGetModel) => getProductName(measurementdevice.productId)}
            />
            <Column
                header={text('lblActive')}
                body={(measurementDevice: MeasurementDevicesGetModel) =>
                    measurementDevice.active ? <Text code="lblActive" /> : <Text code="lblInactiveStatus" />
                }
            />
            <Column
                body={(measurementDevice: MeasurementDevicesGetModel) =>
                    isPartnerDomain ? (
                        <div className="flex justify-content-end">
                            <Button
                                data-testid="btn-remove"
                                tooltip={
                                    measurementDevice.loadBalancerGroupId! > 0
                                        ? text('msgAmpGuardConnected')
                                        : undefined
                                }
                                tooltipOptions={{ showOnDisabled: true }}
                                onClick={() => removeDevice && removeDevice(measurementDevice)}
                                disabled={measurementDevice.loadBalancerGroupId! > 0}
                                title={
                                    measurementDevice.loadBalancerGroupId !== 0
                                        ? text('msgAmpGuardConnected')
                                        : text('btnRemove')
                                }
                                rounded
                                size="small"
                                outlined
                                icon="pi pi-times"
                                severity="danger"
                            />
                        </div>
                    ) : null
                }
            />
        </Table>
    )
})
